import { useTranslation } from "react-i18next";
import { PokerCard } from "@/features/poker/components/PokerCard";
import { Badge } from "@/components/ui/badge";
import { Coins } from "lucide-react";
import Logo from "@/assets/images/logo.png";
import { lt } from "../localize";
import type { SimulationData } from "../types";

interface SimulationBoardProps {
  simulation: SimulationData;
  isAdjusted?: boolean;
}

export const SimulationBoard = ({
  simulation,
  isAdjusted,
}: SimulationBoardProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <div className="space-y-3">
      {isAdjusted && (
        <Badge className="bg-secondary text-secondary-foreground text-xs">
          {t("learn_variant")}
        </Badge>
      )}

      {/* Poker table visual */}
      <div className="relative w-full" style={{ height: 260 }}>
        {/* Felt */}
        <div className="absolute inset-0 rounded-[48%] bg-gradient-to-b from-emerald-800/90 to-emerald-900 shadow-2xl border-[12px] border-black" />
        <div className="absolute inset-4 rounded-[45%] bg-black/20 blur-xl" />

        {/* Center — community cards + pot */}
        <div className="absolute inset-0 flex flex-col items-center justify-start pt-16 gap-2 z-10">
          <img
            src={Logo}
            alt=""
            className="absolute inset-0 m-auto h-24 w-24 opacity-10 pointer-events-none"
          />

          {/* Community cards */}
          {simulation.board.length > 0 ? (
            <div className="relative z-10 flex items-center gap-1">
              {simulation.board.map((card, i) => (
                <PokerCard key={i} card={card} compact />
              ))}
              {Array.from({ length: 5 - simulation.board.length }).map(
                (_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="h-11 w-7 rounded-sm border border-dashed border-white/15"
                  />
                ),
              )}
            </div>
          ) : (
            <div className="relative z-10 text-xs text-white/40 font-medium">
              Preflop
            </div>
          )}

          {/* Pot */}
          <div className="relative z-10 flex items-center gap-1 rounded-full bg-black/40 px-3 py-1 backdrop-blur">
            <Coins className="h-3 w-3 text-secondary" />
            <span className="text-xs font-bold text-secondary">
              {simulation.potSize}
            </span>
          </div>
        </div>

        {/* Hero seat — bottom center */}
        <div
          className="absolute z-20 flex flex-col items-center"
          style={{ bottom: 10, left: "50%", transform: "translateX(-50%)" }}
        >
          <div className="flex items-center gap-1 rounded-lg border border-amber-400 bg-black/70 px-2 py-1.5 shadow-lg ring-1 ring-amber-400/20">
            <div className="flex gap-0.5">
              {simulation.hand.map((card, i) => (
                <PokerCard key={i} card={card} compact />
              ))}
            </div>
            <div className="flex flex-col items-center ml-1">
              <span className="text-[9px] font-bold text-amber-400">
                {simulation.position}
              </span>
              <span className="text-[8px] text-white/60">
                {simulation.stackSize}
              </span>
            </div>
          </div>
        </div>

        {/* Villain indicator — top center (if villain action exists) */}
        {simulation.villainAction && (
          <div
            className="absolute z-20 flex flex-col items-center"
            style={{ top: 8, left: "50%", transform: "translateX(-50%)" }}
          >
            <div className="rounded-lg border border-white/20 bg-black/70 px-3 py-1.5 shadow-lg">
              <span className="text-[10px] font-semibold text-red-400">
                {simulation.villainAction}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground leading-relaxed px-1">
        {lt(simulation.description, lang)}
      </p>

      {/* Highlights */}
      {simulation.highlights && simulation.highlights.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1">
          {simulation.highlights.map((h, i) => (
            <Badge
              key={i}
              variant="outline"
              className="text-xs text-secondary border-secondary/40"
            >
              {lt(h, lang)}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
